import React, {memo, useEffect} from 'react';
import './styles.css'
import {DropdownInput, DropdownOption, Text, TextInput} from "atmosfair-ui";
import {AircraftTypeResponse, AirlineResponse, AirportResponse} from "../../../../api/model/activity-flight";
import {Leg} from "../../../../types-enums/activity-flight";
import DatePicker from "react-datepicker"
import {useTranslation} from "react-i18next";
import {DateTime} from "luxon";
import AutocompleteQuery, {
    AUTOCOMPLETE_ACTIVITY_TYPE,
    AUTOCOMPLETE_QUERY_TYPE,
    AutocompleteQueryProps
} from '../../../../molecules/autocomplete-query/AutocompleteQuery';

import {TravelClass, TravelClassForDropdown} from "../../../../screens/activity/activity-form/flight";
import {
    convertAircraftTypeFromDropdownFormat,
    convertAircraftTypeToDropdownFormat,
    convertTravelClassesFromDropdownFormat,
    convertTravelClassesToDropdownFormat
} from "../../../../utils/converter/dropdown";
import {Tooltip} from "@mui/material";
import {setDateFormat} from "../../../../utils/setter/common";
import clsx from "clsx";

export interface FlightSelectorProps {
    airportError: AutocompleteQueryProps["error"],
    aircraftsNetworkError: boolean,
    flightNumberError: boolean,
    airportLabel: AutocompleteQueryProps["label"],
    leg: Leg,
    setAirport: (airport: AirportResponse) => void,
    onBlurFlightNumber: (flightNumber: string | null | undefined) => void,
    onFocusFlightNumber?: () => void,
    onBlurFlightDate: () => void,
    required: AutocompleteQueryProps["required"],
    setAircraftType: (aircraftType: AircraftTypeResponse | null) => void,
    setTravelClass: (travelClass: TravelClass | null) => void,
    setFlightNumber: (flightNumber: string | null) => void,
    setAirline: (airline: AirlineResponse | null) => void,
    onCalenderClose?: () => void,
    onCalenderFocus?: () => void,
    aircrafts: AircraftTypeResponse[],
    travelClasses: TravelClassForDropdown[],
    setDate: (date: DateTime | null) => void,
    minDate?: DateTime
    maxDate?: DateTime
    openToDate?: DateTime
    disableDateRangePicker: boolean,
}

const FlightSelector = memo(function ({
                                          airportError,
                                          aircraftsNetworkError,
                                          flightNumberError,
                                          airportLabel,
                                          leg,
                                          setAirport,
                                          setAircraftType,
                                          setTravelClass,
                                          setFlightNumber,
                                          onBlurFlightNumber,
                                          onBlurFlightDate,
                                          onFocusFlightNumber,
                                          required,
                                          travelClasses,
                                          setDate,
                                          onCalenderClose,
                                          onCalenderFocus,
                                          aircrafts,
                                          setAirline,
                                          minDate,
                                          maxDate,
                                          openToDate,
                                          disableDateRangePicker,
                                      }: FlightSelectorProps) {
    const {t, i18n} = useTranslation()
    const {resolvedLanguage} = i18n
    const [calendarOpen, setCalendarOpen] = React.useState(false)
    const [aircraftsDropdownOptions, setAircraftsDropdownOptions] = React.useState<DropdownOption[]>([])
    const travelClassesDropdownOptions = React.useRef<DropdownOption[]>(convertTravelClassesToDropdownFormat(travelClasses))

    useEffect(() => {
        setAircraftsDropdownOptions(convertAircraftTypeToDropdownFormat(aircrafts))
    }, [aircrafts]);

    const _convertFlightNumber = (flightNumber: string | null) => {
        if (flightNumber === null || flightNumber.length === 0) return null
        return flightNumber
    }

    const _getDefaultAircraftOption = (
        dropdownOptions: DropdownOption[],
        selectedValue: string | null | undefined
    ): number | null => dropdownOptions.find(option => option.raw.iata === selectedValue)?.value ?? null

    const _getDefaultTravelClassOption = (
        dropdownOptions: DropdownOption[],
        selectedValue: TravelClass | undefined | null
    ): number | null => dropdownOptions.find(option => option.raw.value === selectedValue)?.value ?? null

    const _setLabelColor = () => {
        if (disableDateRangePicker) return "lightGreyDarker"
        if (calendarOpen) return "primary"
        return "black"
    }

    const _renderDate = () => {
        const DateContent = <div
            style={disableDateRangePicker ? {width: "unset"} : {}}
            className={"flight-selector-datepicker-wrapper"}>
            <Text
                color={_setLabelColor()}
                variant={"label"}>
                ACTIVITIES.FLIGHT.DEPARTURE_DATE
            </Text>
            <DatePicker
                disabled={disableDateRangePicker}
                minDate={minDate?.toJSDate() ?? undefined}
                maxDate={maxDate?.toJSDate() ?? undefined}
                openToDate={openToDate?.toJSDate() ?? undefined}
                onFocus={onCalenderFocus}
                closeOnScroll={true}
                onCalendarClose={() => {
                    setCalendarOpen(false)
                    onCalenderClose && onCalenderClose()
                }}
                onBlur={onBlurFlightDate}
                onCalendarOpen={() => setCalendarOpen(true)}
                dateFormat={setDateFormat(resolvedLanguage)}
                placeholderText={t('ACTIVITIES.FLIGHT.DEPARTURE_DATE')}
                selected={leg.date ? leg.date.toJSDate() : null}
                onChange={date => setDate(date ? DateTime.fromJSDate(date) : null)}/>
        </div>

        if (disableDateRangePicker) return <Tooltip placement={"top"}
                                                    title={t("TOOLTIP.COMMON.CALENDAR_DEACTIVATED_JOURNEY_NO_DATES")}>
            <div className={"flight-selector-datepicker-wrapper"}>{DateContent}</div>
        </Tooltip>
        return DateContent
    }

    return (
        <div
            className={clsx('flight-selector-container', 'form-row')}>
            <AutocompleteQuery
                containerStyle={{flex: 1}}
                placeholder={"ACTIVITIES.FLIGHT.PLACEHOLDER_AIRPORT"}
                aria-errormessage={"ERRORS.FLIGHT.SELECT_AIRPORT_FROM_LIST"}
                type={AUTOCOMPLETE_ACTIVITY_TYPE.flight}
                required={required}
                defaultValue={leg.airport}
                error={airportError}
                label={airportLabel}
                queryType={AUTOCOMPLETE_QUERY_TYPE.airport}
                setSelection={airport => setAirport(airport as AirportResponse)}/>
            {_renderDate()}
            <TextInput
                aria-errormessage={'ERRORS.FLIGHT.FLIGHT_NUMBER_INVALID'}
                value={leg.flightNumber ?? undefined}
                error={flightNumberError}
                onBlur={event => {
                    const {value} = event.target
                    const flightNumber = _convertFlightNumber(value.toUpperCase().trim())
                    onBlurFlightNumber(flightNumber)
                    setFlightNumber(flightNumber)
                }}
                onFocus={onFocusFlightNumber}
                containerStyle={{flex: 1}}
                onChange={event => {
                    const {value} = event.target
                    setFlightNumber(_convertFlightNumber(value.toUpperCase()))
                }}
                placeholder={"ACTIVITIES.FLIGHT.FLIGHT_NUMBER"}
                label={"ACTIVITIES.FLIGHT.FLIGHT_NUMBER"}/>
            <AutocompleteQuery
                placeholder={"GENERAL.AIRLINE"}
                aria-errormessage={"ERRORS.FLIGHT.SELECT_AIRLINE_FROM_LIST"}
                type={AUTOCOMPLETE_ACTIVITY_TYPE.flight}
                required={false}
                defaultValue={leg.airline}
                queryType={AUTOCOMPLETE_QUERY_TYPE.airline}
                label={"GENERAL.AIRLINE"}
                containerStyle={{flex: 1}}
                setSelection={airline => setAirline(airline as AirlineResponse)}/>
            <DropdownInput
                label={"ACTIVITIES.FLIGHT.AIRCRAFT_TYPE"}
                options={aircraftsDropdownOptions}
                dataError={aircraftsNetworkError}
                placeholder={"GENERAL.OPTION_OPTIONAL"}
                setSelection={value => setAircraftType(convertAircraftTypeFromDropdownFormat(value))}
                aria-errormessage={"ERRORS.GENERAL_NETWORK_ERROR_BODY"}
                errorMessage={"ERRORS.GENERAL_NETWORK_ERROR_BODY"}
                containerStyle={{flex: 1}}
                showDeleteIcon={true}
                defaultValue={_getDefaultAircraftOption(aircraftsDropdownOptions, leg?.aircraftType?.iata) || null}/>
            <DropdownInput
                label={"ACTIVITIES.FLIGHT.TRAVEL_CLASS"}
                options={travelClassesDropdownOptions.current}
                placeholder={"GENERAL.OPTION_OPTIONAL"}
                setSelection={value => setTravelClass(convertTravelClassesFromDropdownFormat(value))}
                containerStyle={{flex: 1}}
                showDeleteIcon={true}
                defaultValue={_getDefaultTravelClassOption(travelClassesDropdownOptions.current, leg?.travelClass) || null}/>
        </div>
    )
})

export default FlightSelector